/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { ContentState, RawDraftContentBlock, convertToRaw } from "draft-js";
import { BaseRange, BaseSelection, Descendant, Editor, Range, Text, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import { DOMRange } from "slate-react/dist/utils/dom";

import { TextBlockSnapshot } from "@vienna/common/src";

import { SlashMenuOption } from "../../SlashMenu/slashMenuOptions";
import { CustomElement } from "src/declare";

import { createList, deleteListItem, indentLists, outdentLists } from "./lists";
import { MarkType } from "./types";

/**
 * An enhanced editor
 */
export const EnhancedEditor = {
  // toggles formatting of bold, underline, and italic
  toggleMark(editor: Editor, format: MarkType): void {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  },
  bulletedList(editor: Editor): void {
    createList(editor, "bulleted-list");
  },
  numberedList(editor: Editor, start: number = 1): void {
    createList(editor, "numbered-list", start);
  },
  // merges all the lists in the editor
  mergeLists(
    editor: Editor,
    selection: any = Editor.range(editor, Editor.start(editor, []), Editor.end(editor, [])),
    depth: number = 0
  ): void {
    // holds the previous node path and type
    let prevTypeAndPath: [any, number[]] = [null, []];
    // for each node in the selection
    forEachNodeInSelection(
      editor,
      selection,
      (currentSelection: any, type: any) => {
        prevTypeAndPath = handleMergeLists(editor, currentSelection, type, prevTypeAndPath, depth);
      },
      true
    );
  },
  getFilteredSlashMenuOptions(options: SlashMenuOption[], range: Range, editor: Editor): SlashMenuOption[] | null {
    if (range) {
      const commandWithSlash = Editor.string(editor, range); // get the command from the command range
      const lastSlashIndex = commandWithSlash.lastIndexOf("/"); // find the last slash in the command
      const command = commandWithSlash.slice(lastSlashIndex + 1, commandWithSlash.length); // grab the text after the last slash

      if (isWhitespace(command) || commandWithSlash === command) {
        // Make sure the command is not empty, and contains a slash
        return null;
      }
      const filteredOptions = options.filter((option) =>
        option.name.toLowerCase().includes(command.toLowerCase())
      ); // filter the options to only include those that include the command
      return filteredOptions.length ? filteredOptions : null; // return the filtered options or null if there are no options
    }
    return null;
  },
  resetFonts(editor: Editor): void {
    // reset fonts on all nodes in the editor
    // create a node generator under the condition that the node has text
    const nodes = Editor.nodes(editor, { at: [], match: (n: any) => n.text });
    // store the current node in a variable
    let node = nodes.next();
    // while there are nodes to process
    while (!node.done) {
      // set the font of the current node to inherit
      Transforms.setNodes(editor, { font: "inherit" }, { at: node.value[1] });
      // move to the next node
      node = nodes.next();
    }
  },
};

const handleMergeLists = (
  editor: Editor,
  selection: any,
  type: string,
  prevTypeAndPath: [string, number[]],
  depth: number = 0
): [string, number[]] => {
  if (prevTypeAndPath[0] === type && (type === "bulleted-list" || type === "numbered-list")) {
    // If the previous node is the same type as the current node and they are mergeable, merge them
    Transforms.mergeNodes(editor, { at: prevTypeAndPath[1] });
  }
  const path = selection.anchor.path.slice(0, depth + 1);
  if (type === "bulleted-list" || type === "numbered-list") {
    // if the node is a list, try to merge lists within this list
    const edges = Editor.edges(editor, path);
    EnhancedEditor.mergeLists(editor, Editor.range(editor, edges[0], edges[1]), depth + 1);
  }
  // update the previous node path and type
  return [type, path];
};

/**
 * Checks if a mark of a given type is active in the editor.
 */
const isMarkActive = (editor: Editor, format: MarkType): boolean => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const hasCommandModifier = (event: React.KeyboardEvent): boolean => {
  return event.ctrlKey || event.metaKey;
};

// This function is needed as if the previous node is empty, slate seems to bug out
export const handleBackspace = (editor: Editor, e: React.KeyboardEvent): "handled" | void => {
  if (editor.selection) {
    // if the selection is collapsed
    if (Range.isCollapsed(editor.selection)) {
      // if we are at the start of a list item, delete the list item
      const path = editor.selection.anchor.path;
      if (editor.selection.anchor.offset === 0 && path[path.length - 1] === 0 && path.length > 2) {
        deleteListItem(editor, editor.selection);
        Transforms.collapse(editor, { edge: "start" });
        e.preventDefault();
        return;
      }
      // if we are at the start of a text line, delete the line
      if (editor.selection.anchor.offset === 0 && editor.children.length > 1) {
        // save the text and path of the current line
        const node: any = Editor.node(editor, [path[0]]);
        // one more check - if we are at the start of the editor, and the current node has text, don't do anything
        if (path[0] === 0 && node[0].text) {
          e.preventDefault();
          return;
        }
        // delete the line
        Transforms.removeNodes(editor, { at: [path[0]] });
        try {
          // try to select the previous
          Transforms.select(editor, Editor.start(editor, path));
          Transforms.move(editor, { unit: "character", reverse: true });
        } catch {
          // dont do anything if there is no previous line
        }
        // store the selection of the new current line
        const selection = editor.selection;
        // insert the text of the line we just deleted
        node[0].children.map((child: any) => {
          Transforms.insertNodes(editor, child);
        });
        // set the cursor back the the offset we had before inserting the text
        Transforms.select(editor, selection);
        e.preventDefault();
        return;
      }
      if (editor.selection.anchor.offset === 0 && editor.children.length === 1) {
        // if we are at the start of the editor, and there is no content, reset the font and link marks and stop slate from doing anything stupid
        Editor.removeMark(editor, "link");
        Editor.removeMark(editor, "font");
        e.preventDefault();
      }
      return;
    }
    // handle deleting a selection
    e.preventDefault();
    const edges = Editor.edges(editor, editor.selection);
    // if deleting text in bullet point, keep the bullet point
    if (
      edges[0].offset === 0 &&
      edges[0].path.length > 2 &&
      JSON.stringify(edges[0].path) !== JSON.stringify(edges[1].path)
    ) {
      // insert a space before the selection
      Transforms.insertText(editor, " ", { at: { path: edges[0].path, offset: 0 } });
      // delete the selection (not including space)
      Transforms.delete(editor, {
        at: Editor.range(editor, { path: edges[0].path, offset: 1 }, edges[1]),
      });
      // delete the space
      Editor.deleteBackward(editor, { unit: "character" });
      return;
    }
    // otherwise, delete the entire selection
    const fragment: any = editor.getFragment();
    Transforms.delete(editor, { at: Editor.range(editor, edges[0], edges[1]) });
    // set the selection to type plain to avoid list items not in bullet points
    if (fragment[0].type === "plain") Transforms.setNodes(editor, { type: "plain" }, { at: editor.selection });
    return;
  }
};

export const handleEnter = (editor: Editor, e: React.KeyboardEvent): void => {
  if (editor.selection) {
    // if the selection is collapsed
    if (Range.isCollapsed(editor.selection)) {
      // if we are at the start of an empty list item, outdent the list item
      const node: any = Editor.node(editor, editor.selection.anchor.path);
      const path = editor.selection.anchor.path;
      if (
        editor.selection.anchor.offset === 0 &&
        path[path.length - 1] === 0 &&
        path.length > 2 &&
        node[0].text === ""
      ) {
        outdentLists(editor, editor.selection);
        e.preventDefault();
      }
    }
  }
};

export const handleTab = (editor: Editor, e: React.KeyboardEvent): void => {
  // we're going to handle every tab manually
  const selection = editor.selection;
  if (selection) {
    // indent the selection
    indentLists(editor);
    e.preventDefault();
  }
};

// function to itterate over each node at the same level in the selection
export const forEachNodeInSelection = (
  editor: Editor,
  selection: BaseRange,
  callback: (selection: BaseRange, type: string | undefined) => any,
  reverse: boolean = false
): void => {
  // get the node at the selection
  const nodeData = Editor.node(editor, selection);
  const node = nodeData[0] as Descendant;
  const path = nodeData[1];

  // get the start and end points of the selection
  const start = Editor.start(editor, selection);
  const end = Editor.end(editor, selection);

  // Start at the start/end depending on the reverse flag
  if (reverse) path.push(end.path[path.length]);
  else path.push(start.path[path.length]);

  // if the Node does not have any children, return
  if (!(node as CustomElement).children) {
    const parentNode: any = Editor.parent(editor, selection)[0];
    switch (parentNode.type) {
      case "list-item":
        callback(selection, "list-item");
        break;
      default:
        callback(selection, undefined);
        break;
    }
    return;
  }

  // while you are still in the selection, itterate over each child node
  while (
    reverse
      ? path[path.length - 1] >= start.path[path.length - 1]
      : path[path.length - 1] <= end.path[path.length - 1]
  ) {
    // get the edges of the current child
    const pathEdges = Editor.edges(editor, path);
    // make the start the max of start or the path edges start
    pathEdges[0] = Editor.end(editor, { anchor: pathEdges[0], focus: start });
    // make teh end the min of end or the path edges end
    pathEdges[1] = Editor.start(editor, { anchor: pathEdges[1], focus: end });

    // perform function on the section
    callback(
      Editor.range(editor, pathEdges[0], pathEdges[1]),
      ((node as CustomElement).children[path[path.length - 1]] as unknown as CustomElement).type
    );

    // move the path to the next child
    path[path.length - 1] += reverse ? -1 : 1;
  }
};

export const handleShiftTab = (
  editor: Editor,
  e: React.KeyboardEvent,
  selection: BaseSelection = editor.selection
): "handled" | void => {
  if (selection) {
    e.preventDefault();
    // outdent all the lists in the selection
    outdentLists(editor, selection);
  }
};

const isWhitespace = (str: string): boolean => {
  if (!str) return false;
  return str.trim().length === 0;
};

export const getTextContent = (block: TextBlockSnapshot): Descendant[] => {
  if (block.textContent.length) {
    return block.textContent;
  } else if (block.rawContent?.blocks) {
    return draftBlocksToSlateChildren(block.rawContent.blocks);
  } else {
    return draftBlocksToSlateChildren(convertToRaw(ContentState.createFromText(block.plainText || "")).blocks);
  }
};
// This takes draft blocks and converts it into slate children
export const draftBlocksToSlateChildren = (
  draftBlocks: RawDraftContentBlock[],
  depth: number = 0
): Descendant[] => {
  // create an array to hold the children
  const children: any[] = [];
  // for each block in the draft blocks
  draftBlocks.map((draftBlock: any) => {
    // create something to store text
    const text = { type: "plain", children: [] as Text[] };
    // if the block is an unordered list:
    if (draftBlock.type === "unordered-list-item" && depth <= draftBlock.depth + 1) {
      // check if we are deep enough into the list to create the list item
      if (depth === draftBlock.depth + 1) {
        // if we are, create the list item
        children.push({
          type: "list-item",
          children: (draftBlocksToSlateChildren([draftBlock], depth + 1)[0] as unknown as Element).children || [],
        });
        return;
      }
      // otherwise, we need to create a bulleted list and recursively call this function with depth increased by 1
      children.push({
        type: "bulleted-list",
        children: draftBlocksToSlateChildren([draftBlock], depth + 1),
      });
      return;
    }
    // if the block is an ordered list:
    if (draftBlock.type === "ordered-list-item" && depth <= draftBlock.depth + 1) {
      // check if we are deep enough into the list to create the list item
      if (depth === draftBlock.depth + 1) {
        // if we are, create the list item
        children.push({ type: "list-item", children: draftBlocksToSlateChildren([draftBlock], depth + 1) });
        return;
      }
      // otherwise, we need to create a numbered list and recursively call this function with depth increased by 1
      children.push({
        type: "numbered-list",
        children: draftBlocksToSlateChildren([draftBlock], depth + 1),
      });
      return;
    }
    // Otherwise, we are looking at a plain text block
    // create an array to hold all the points where the styles change, and which styles they are
    const breaks = draftBlock.inlineStyleRanges.map((style: any) => {
      return [
        [style.offset, style.style],
        [style.offset + style.length, style.style],
      ];
    });
    // flatten the breaks array
    const flatBreaks = breaks.flat();
    // sort the flattened breaks array by the first element in each pair
    flatBreaks.sort((a: any, b: any) => (a[0] < b[0] ? -1 : 1));
    // start at the beginning of the text
    let current = 0;
    // set all the styles to false
    let bold = false;
    let italic = false;
    let underline = false;
    // for each break in the flattened breaks array
    flatBreaks.map((breakPoint: any) => {
      // if there is any text to push, push it with all the current styles
      if (draftBlock.text.slice(current, breakPoint[0] as number)) {
        text.children.push({
          bold: bold ? bold : undefined,
          italic: italic ? italic : undefined,
          underline: underline ? underline : undefined,
          text: draftBlock.text.slice(current, breakPoint[0] as number),
        });
      }
      // if any styles have changed, set the current styles to the new styles
      if (breakPoint[1] === "BOLD") {
        bold = !bold;
      }
      if (breakPoint[1] === "ITALIC") {
        italic = !italic;
      }
      if (breakPoint[1] === "UNDERLINE") {
        underline = !underline;
      }
      // set the current to the break point
      current = breakPoint[0] as number;
    });
    // push the final text with the current styles
    text.children.push({
      bold: bold ? bold : undefined,
      italic: italic ? italic : undefined,
      underline: underline ? underline : undefined,
      text: draftBlock.text.slice(current, draftBlock.text.length),
    });
    // add the text to the children array
    children.push(text);
    return;
  });
  // return the children array
  return children;
};

export const scrollSlateIntoView = (editor: ReactEditor, domRange: DOMRange): void => {
  // Scrolling seems to be broken in slate, so we do it manually. This is still far from perfect though...
  if (editor.selection && Range.isCollapsed(editor.selection)) {
    const leafEl = domRange.startContainer.parentElement!;
    leafEl.getBoundingClientRect = domRange.getBoundingClientRect.bind(domRange);
    if (leafEl.getBoundingClientRect().top < 0 || leafEl.getBoundingClientRect().bottom > window.innerHeight) {
      leafEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // @ts-expect-error an unorthodox delete D:
    delete leafEl.getBoundingClientRect;
  }
};
